/**
 * Auto-save and restore hook for strategy state.
 *
 * Mirrors useSizingPersistence: restores on STRATEGY_INITIALIZED,
 * marks dirty on mutation events, saves via workflowState.upsert.
 */

import { useEffect, useRef, useState } from 'react';

import { useApi, useVisualization } from '@/context';
import { useAutoSave, type PersistenceState } from '@/hooks/use-auto-save';
import { notifyWorkflowSaveRefused } from '@/lib/workflow-save-error';
import { ApiError } from '@/services';
import type { IStrategyStateDTO } from '@/workflows/vascular/common/catalog';
import { StrategyStateService } from '@/workflows/vascular/evar/services/strategy-state-service';

const WORKFLOW_STATE_KEY = 'strategy';

// ============================================================================
// Types
// ============================================================================

export interface UseStrategyPersistenceOptions {
    caseId: string;
}

export type UseStrategyPersistenceResult = PersistenceState;

// ============================================================================
// Hook
// ============================================================================

export function useStrategyPersistence(options: UseStrategyPersistenceOptions): UseStrategyPersistenceResult {
    const { caseId } = options;
    const api = useApi();
    const { getServiceManager } = useVisualization();
    const isRestoredRef = useRef(false);
    const [restoreError, setRestoreError] = useState<Error | null>(null);

    // Resolve service from the ServiceManager
    const sm = getServiceManager();
    const strategyService = sm.getService<StrategyStateService>(StrategyStateService.SERVICE_NAME);

    // ── Auto-save setup ──
    const autoSave = useAutoSave<IStrategyStateDTO>({
        debounceMs: 1500,
        maxRetries: 3,
        // A 403 refusal must reach the person losing the edits (ESCLOUD-288).
        onError: notifyWorkflowSaveRefused,
        getState: () => strategyService.serializeState(),
        saveFn: async (state, version) => {
            const response = await api.workflowState.upsert(caseId, WORKFLOW_STATE_KEY, {
                state,
                version,
            });
            return { version: response.version };
        },
    });

    // ── Restore state from API after strategy initialization ──
    useEffect(() => {
        const restoreFromApi = async () => {
            if (isRestoredRef.current) return;
            isRestoredRef.current = true;

            try {
                const saved = await api.workflowState.getByKey(caseId, WORKFLOW_STATE_KEY);
                if (saved) {
                    autoSave.setVersion(saved.version);
                    const dto = saved.state as IStrategyStateDTO;
                    strategyService.loadState(dto);
                }
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) {
                    return;
                }
                console.error('[StrategyPersistence] Failed to restore state:', error);
                setRestoreError(error instanceof Error ? error : new Error(String(error)));
            }
        };

        const unsubscribe = strategyService.subscribe(
            strategyService.EVENTS.STRATEGY_INITIALIZED,
            restoreFromApi
        );

        // If already initialized, restore immediately
        if (strategyService.isInitialized()) {
            restoreFromApi();
        }

        return () => {
            unsubscribe();
            isRestoredRef.current = false;
        };
    }, [caseId, strategyService]);

    // ── Subscribe to mutation events -> markDirty ──
    useEffect(() => {
        const unsubscribers = strategyService.MUTATION_EVENTS.map((event) =>
            strategyService.subscribe(event, autoSave.markDirty)
        );

        return () => {
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [strategyService, autoSave.markDirty]);

    return {
        status: autoSave.status,
        saveNow: autoSave.saveNow,
        conflict: autoSave.conflict,
        dismissConflict: autoSave.dismissConflict,
        lastSavedAt: autoSave.lastSavedAt,
        retryCount: autoSave.retryCount,
        restoreError,
    };
}
